export const GOONDECK_CARD_TYPES = ['character', 'spell', 'trap', 'support'] as const;
export type GoonDeckCardType = (typeof GOONDECK_CARD_TYPES)[number];

export const GOONDECK_RANGES = ['melee', 'ranged'] as const;
export type GoonDeckRange = (typeof GOONDECK_RANGES)[number];

export const GOONDECK_ATTRIBUTES = [
  'neutral',
  'fire',
  'water',
  'storm',
  'shadow',
  'light',
  'tech',
  'dream',
] as const;
export type GoonDeckAttribute = (typeof GOONDECK_ATTRIBUTES)[number];

export const GOONDECK_FOILS = [
  'standard',
  'silver',
  'gold',
  'holo',
  'rainbow',
  'starfield',
  'glitch',
  'prismatic',
  'mythic',
] as const;
export type GoonDeckFoil = (typeof GOONDECK_FOILS)[number];

export const GOONDECK_UNLOCK_RANKS = [
  { id: 'buddy', label: 'Buddy' },
  { id: 'friend', label: 'Friend' },
  { id: 'close_friend', label: 'Close Friend' },
  { id: 'homie', label: 'Homie' },
  { id: 'trusted', label: 'Trusted' },
  { id: 'intimate', label: 'Intimate' },
  { id: 'lover', label: 'Lover' },
  { id: 'bonded', label: 'Bonded' },
] as const;
export type GoonDeckUnlockRank = (typeof GOONDECK_UNLOCK_RANKS)[number]['id'];

export const GOONDECK_ABILITY_KEYS = [
  'none',
  'bonus_damage',
  'heal_ally',
  'add_shield',
  'poison',
  'burn',
  'stun',
  'charm',
  'silence',
  'cleanse',
  'row_hit',
  'pull_forward',
  'push_back',
  'draw_card',
  'gain_energy',
] as const;
export type GoonDeckAbilityKey = (typeof GOONDECK_ABILITY_KEYS)[number];

export interface GoonDeckAbilityTemplate {
  key: GoonDeckAbilityKey;
  name: string;
  description: string;
  baseCost: number;
  maxPower: number;
}

export interface GoonDeckCardDraft {
  companionId: string;
  cardName: string;
  cardType: GoonDeckCardType;
  attribute: GoonDeckAttribute;
  foil: GoonDeckFoil;
  hp: number;
  attack: number;
  shield: number;
  range: GoonDeckRange;
  abilityKey: GoonDeckAbilityKey;
  abilityPower: number;
  abilityText: string;
  flavorText: string;
  frontImageUrl: string | null;
  backImageUrl: string | null;
  artBrightness: number;
  artContrast: number;
  artEmboss: number;
  artEmbossDirection: number;
  artEmbossThreshold: number;
  /** Art frames cycled through by tilt angle when this card's foil preset
   * turns tiltFrames on — see 20261241000000. frontImageUrl stays the
   * resting art. Empty (or a single entry) means an ordinary card. */
  tiltFrames: string[];
}

export interface GoonDeckBalanceResult {
  budget: number;
  used: number;
  remaining: number;
  valid: boolean;
  warnings: string[];
}

export const GOONDECK_ABILITY_TEMPLATES: Record<GoonDeckAbilityKey, GoonDeckAbilityTemplate> = {
  none: {
    key: 'none',
    name: 'No ability',
    description: 'Pure stats, no special effect.',
    baseCost: 0,
    maxPower: 0,
  },
  bonus_damage: {
    key: 'bonus_damage',
    name: 'Bonus damage',
    description: 'Deal extra damage with the next attack.',
    baseCost: 2,
    maxPower: 6,
  },
  heal_ally: {
    key: 'heal_ally',
    name: 'Heal ally',
    description: 'Restore HP to one allied card.',
    baseCost: 3,
    maxPower: 6,
  },
  add_shield: {
    key: 'add_shield',
    name: 'Add shield',
    description: 'Give shield to yourself or one allied card.',
    baseCost: 2,
    maxPower: 6,
  },
  poison: {
    key: 'poison',
    name: 'Poison',
    description: 'Apply damage over time for a few turns.',
    baseCost: 3,
    maxPower: 4,
  },
  burn: {
    key: 'burn',
    name: 'Burn',
    description: 'Apply damage over time that pushes through some shield.',
    baseCost: 4,
    maxPower: 4,
  },
  stun: {
    key: 'stun',
    name: 'Stun',
    description: 'Make one target skip its next action.',
    baseCost: 6,
    maxPower: 1,
  },
  charm: {
    key: 'charm',
    name: 'Charm',
    description: 'Temporarily turn one enemy action in your favor.',
    baseCost: 7,
    maxPower: 1,
  },
  silence: {
    key: 'silence',
    name: 'Silence',
    description: 'Stop one target from using abilities next turn.',
    baseCost: 4,
    maxPower: 1,
  },
  cleanse: {
    key: 'cleanse',
    name: 'Cleanse',
    description: 'Remove negative effects from an allied card.',
    baseCost: 3,
    maxPower: 1,
  },
  row_hit: {
    key: 'row_hit',
    name: 'Row hit',
    description: 'Hit every enemy in one row for light damage.',
    baseCost: 5,
    maxPower: 4,
  },
  pull_forward: {
    key: 'pull_forward',
    name: 'Pull forward',
    description: 'Move a back-row enemy into the front row.',
    baseCost: 3,
    maxPower: 1,
  },
  push_back: {
    key: 'push_back',
    name: 'Push back',
    description: 'Move a front-row target into the back row.',
    baseCost: 3,
    maxPower: 1,
  },
  draw_card: {
    key: 'draw_card',
    name: 'Draw card',
    description: 'Draw extra cards.',
    baseCost: 3,
    maxPower: 2,
  },
  gain_energy: {
    key: 'gain_energy',
    name: 'Gain energy',
    description: 'Gain extra energy for playing cards.',
    baseCost: 3,
    maxPower: 2,
  },
};

export const GOONDECK_BUDGET_BY_FOIL: Record<GoonDeckFoil, number> = {
  standard: 10,
  silver: 12,
  gold: 14,
  holo: 16,
  rainbow: 18,
  starfield: 20,
  glitch: 20,
  prismatic: 22,
  mythic: 24,
};

export function defaultGoonDeckCardDraft(companionId = ''): GoonDeckCardDraft {
  return {
    companionId,
    cardName: '',
    cardType: 'character',
    attribute: 'neutral',
    foil: 'standard',
    hp: 6,
    attack: 2,
    shield: 0,
    range: 'melee',
    abilityKey: 'none',
    abilityPower: 0,
    abilityText: '',
    flavorText: '',
    frontImageUrl: null,
    backImageUrl: null,
    artBrightness: 1,
    artContrast: 1,
    artEmboss: 0,
    artEmbossDirection: 45,
    artEmbossThreshold: 0.35,
    tiltFrames: [],
  };
}

export function balanceGoonDeckCard(card: GoonDeckCardDraft): GoonDeckBalanceResult {
  const ability = GOONDECK_ABILITY_TEMPLATES[card.abilityKey] || GOONDECK_ABILITY_TEMPLATES.none;
  const budget = GOONDECK_BUDGET_BY_FOIL[card.foil] || GOONDECK_BUDGET_BY_FOIL.standard;
  const abilityPower = card.abilityKey === 'none' ? 0 : Math.max(1, Math.min(card.abilityPower, ability.maxPower));
  const used =
    Math.max(0, card.hp) +
    Math.max(0, card.attack) * 2 +
    Math.max(0, card.shield) * 2 +
    (card.range === 'ranged' ? 1 : 0) +
    ability.baseCost +
    abilityPower;

  const warnings: string[] = [];
  if (!card.cardName.trim()) warnings.push('Card name is required.');
  if (!card.companionId) warnings.push('Pick an existing GoonPal.');
  if (card.hp < 1) warnings.push('HP should be at least 1.');
  if (card.attack < 0) warnings.push('Attack cannot be negative.');
  if (card.shield < 0) warnings.push('Shield cannot be negative.');
  if (card.abilityKey !== 'none' && abilityPower !== card.abilityPower) warnings.push(`${ability.name} supports power 1-${ability.maxPower}.`);
  if (used > budget) warnings.push(`This card is ${used - budget} points over its ${budget}-point budget.`);

  return {
    budget,
    used,
    remaining: budget - used,
    valid: warnings.length === 0 && used <= budget,
    warnings,
  };
}
